// middleware/rolePermissions.js

// Define role hierarchy and permissions
const roleHierarchy = {
  admin: 3,
  supervisor: 2,
  member: 1,
  guest: 0,
};

// Permission matrix for different roles
const rolePermissions = {
  admin: [
    'manage_users',
    'manage_workspace',
    'view_analytics',
    'manage_content',
    'assign_roles',
    'view_all_reports',
    'manage_settings',
  ],
  supervisor: [
    'view_team_analytics',
    'manage_team_content',
    'view_team_reports',
    'manage_team_assignments',
    'view_workspace',
  ],
  member: [
    'view_own_content',
    'submit_content',
    'view_own_reports',
    'view_workspace',
  ],
  guest: [
    'view_demo_content',
    'view_workspace',
  ],
};

/**
 * Check if a user has permission to perform an action
 * @param {string} userRole - The role of the user
 * @param {string} requiredPermission - The permission to check
 * @returns {boolean} - Whether the user has the required permission
 */
export function hasPermission(userRole, requiredPermission) {
  if (!userRole || !rolePermissions[userRole]) {
    return false;
  }

  return rolePermissions[userRole].includes(requiredPermission);
}

/**
 * Check if a user role has higher or equal privileges than another role
 * @param {string} userRole - The role to check
 * @param {string} requiredRole - The role to compare against
 * @returns {boolean} - Whether the user role has sufficient privileges
 */
export function hasRolePrivilege(userRole, requiredRole) {
  if (!userRole || !requiredRole) {
    return false;
  }

  const userRoleLevel = roleHierarchy[userRole] || 0;
  const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

  return userRoleLevel >= requiredRoleLevel;
}

/**
 * Get all permissions for a given role
 * @param {string} role - The role to get permissions for
 * @returns {string[]} - Array of permissions for the role
 */
export function getRolePermissions(role) {
  return rolePermissions[role] || [];
}

/**
 * Check if a session or user can access super-user-only tooling.
 * Accepts either a NextAuth session object or a session.user-shaped object.
 */
export function canAccessSuperUserTools(sessionOrUser) {
  const user = sessionOrUser?.user || sessionOrUser;
  return user?.superUser === true;
}

/** Reset progress only as a studio admin or a non-impersonating super user. */
export function canResetLearningPathProgress(user) {
  return Boolean(user?.id && (
    hasRolePrivilege(user.role, "admin") ||
    (user.superUser === true && !user.isImpersonated)
  ));
}

/**
 * Whether a session may administer one specific studio: that studio's own
 * admin, or a super user who is not currently impersonating.
 *
 * The impersonation clause is not decoration. While impersonating, the JWT
 * still carries the original super user's `superUser: true`, so that flag
 * alone would hand an impersonated session the admin screens of whichever
 * studio it landed in. Page guards and the server actions behind them must
 * agree on this, or the UI offers a form the action will reject.
 *
 * Accepts either a NextAuth session object or a session.user-shaped object.
 */
export function canAdministerWorkspace(sessionOrUser, workspaceId) {
  const user = sessionOrUser?.user || sessionOrUser;
  if (!user || !workspaceId) return false;

  const isOwnAdmin = user.role === "admin" && user.workspaceId === workspaceId;
  const isSuperUser = user.superUser === true && !user.isImpersonated;
  return isOwnAdmin || isSuperUser;
}

/**
 * Check whether a session or user can start an impersonation session.
 * Target-level scope is enforced separately by the server action.
 */
export function canImpersonateUsers(sessionOrUser) {
  const user = sessionOrUser?.user || sessionOrUser;
  return (
    user?.superUser === true ||
    ["admin", "supervisor"].includes(user?.role)
  );
}

/**
 * Check whether an actor may impersonate a specific target. Studio boundaries
 * are still verified by the server action because session data is untrusted.
 */
export function canImpersonateUser(sessionOrUser, targetUser) {
  const actor = sessionOrUser?.user || sessionOrUser;
  if (!canImpersonateUsers(actor) || !targetUser) return false;
  if (actor.superUser === true || actor.role === "admin") return true;

  return (
    actor.role === "supervisor" &&
    targetUser.supervisorId === actor.id &&
    canManageUser(actor.role, targetUser.role)
  );
}

/**
 * Check if a user can manage another user based on their roles
 * @param {string} managerRole - The role of the manager
 * @param {string} targetUserRole - The role of the user being managed
 * @returns {boolean} - Whether the manager can manage the target user
 */
export function canManageUser(managerRole, targetUserRole) {
  return hasRolePrivilege(managerRole, targetUserRole) && managerRole !== targetUserRole;
}
